import { LinkCard } from '@/components/admin/liens/link-card';
import { RoleLinkForm } from '@/components/admin/vitrine/role-link-form';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import type { LinkFormDraft } from '@/lib/link-form';
import { emptyLinkForm, linkFormOf, linkFormWithPin, linkPayloadOf } from '@/lib/link-form';
import { linkRefusalOf } from '@/lib/link-labels';
import {
  createLink,
  extendLink,
  fetchExperiencesForLinks,
  fetchLinks,
  revokeLink,
  updateLink,
} from '@/lib/links-api';
import { useIsVitrine } from '@/lib/use-instance';
import type { LinkEntryProblem } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

/**
 * Réglages > Partage (spec §5.1, §11.4, planche `ReglagesPartage`).
 *
 * The links: the list of what is out there, and the creation. A link is the
 * door an operator distributes - an undiscoverable URL that opens one or
 * several experiences, each in its production or development version - and this
 * is the only screen where its secret is ever readable.
 *
 * The PERSON's side of the same object (`/l/<secret>`, the choice page, the
 * session) is issue #60; the backend half it reads, `GET /api/l/<secret>`, is
 * already served.
 *
 * ## `?experience=<id>` pre-fills the creation form (§9.1)
 *
 * « Créer un lien » in the header of an experience's editor lands HERE, with
 * that experience already on the first row. The parameter is validated by the
 * route rather than read raw: an id that names nothing simply leaves the row
 * empty and the select on « Choisir une expérience », which is the honest screen
 * - the alternative would be a row pointing at a ghost, refused a second later
 * by a rule the operator did not break.
 *
 * ## One generation counter, and why it is not two
 *
 * The creation form is reset by a REMOUNT, the régime of
 * `reglages.personas.tsx` and of the two sections it mounts: it owns its draft,
 * initialised from props and never by an effect. The counter moves when a
 * creation LANDS, and on nothing else - a refused creation keeps the rows the
 * operator typed, because the refusal is precisely what they are about to fix.
 *
 * A card being modified is remounted by its own key (`link-card.tsx`), which
 * carries the state a save may have moved.
 */

/**
 * The one search parameter, validated at the route (§9.1).
 *
 * `.catch(undefined)` and not a refusal: a stale link in somebody's chat window
 * must open the screen, not a router error page. What it costs is an empty
 * first row, which is what a fresh form has anyway.
 */
const partageSearchSchema = z.object({
  experience: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/admin/reglages/partage')({
  validateSearch: partageSearchSchema,
  component: ReglagesPartagePage,
});

/**
 * EXPORTED for `tests/reglages-partage.test.tsx`, and for nothing else.
 *
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage REPORT, not out
 * of the suite, and what this component decides - which refusal reaches which
 * form, when a form is reset - cannot be seen from the two components below.
 */
export function ReglagesPartagePage() {
  const { email } = useAdminSession();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  // On the vitrine an entry plays the latest version in production, so the
  // sentence below does not speak of a choice (docs/specs/vitrine.md section 7).
  const vitrine = useIsVitrine();
  const [creationGeneration, setCreationGeneration] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Which card a write is in flight for; `create` for the creation form. */
  const [writing, setWriting] = useState<string | null>(null);

  const links = useQuery({
    queryKey: ['links', email],
    queryFn: () => fetchLinks(),
    retry: false,
  });
  const experiences = useQuery({
    queryKey: ['experiences', email],
    queryFn: () => fetchExperiencesForLinks(),
    retry: false,
  });

  function reread(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['links'] });
  }

  const create = useMutation({
    mutationFn: (draft: LinkFormDraft) => {
      setWriting('create');
      return createLink(linkPayloadOf(draft));
    },
    onSettled: async (_data, error) => {
      setWriting(null);
      await reread();
      // A LANDED creation clears the form; a refused one keeps what was typed,
      // because the refusal is what the operator is about to fix.
      if (error === null || error === undefined) setCreationGeneration((count) => count + 1);
    },
  });

  const save = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: LinkFormDraft }) => {
      setWriting(id);
      return updateLink(id, linkPayloadOf(draft));
    },
    onSettled: async (_data, error) => {
      setWriting(null);
      await reread();
      if (error === null || error === undefined) setEditingId(null);
    },
  });

  const extend = useMutation({
    mutationFn: (id: string) => {
      setWriting(id);
      return extendLink(id);
    },
    onSettled: async () => {
      setWriting(null);
      await reread();
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => {
      setWriting(id);
      return revokeLink(id);
    },
    onSettled: async () => {
      setWriting(null);
      await reread();
    },
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const linksRejected = useRejectOn401(links.error);
  useRejectOn401(experiences.error);

  if (links.isPending) {
    return (
      <div className="flex justify-center py-16">
        <PmsSpinner label="Chargement des liens" />
      </div>
    );
  }

  /*
    The page becomes the error only when there is NOTHING to show - the rule
    `reglages.personas.tsx` states: on an `isError` of a background re-read it
    would throw away a form somebody was filling, although the data was on the
    screen a second earlier.
  */
  if (links.data === undefined) {
    return (
      <p role="alert" className="text-rouge-deep text-sm">
        Les liens n’ont pas pu être lus. Rechargez l’écran.
      </p>
    );
  }

  const staleRead = links.isError && !linksRejected;
  const experienceCards = experiences.data?.experiences ?? [];
  const experiencesRead = experiences.data !== undefined;

  /** The rows the server refused, for the form the refusal belongs to. */
  const problemsOf = (error: unknown): LinkEntryProblem[] => linkRefusalOf(error)?.problems ?? [];
  /** A TRANSPORT failure only: a typed refusal is already drawn under its row. */
  const failureOf = (error: unknown, sentence: string): string | null => {
    if (error === null || error === undefined) return null;
    return linkRefusalOf(error) === null ? sentence : null;
  };

  const now = new Date();
  const origin = window.location.origin;

  return (
    <div className="space-y-10 pb-16">
      <header className="space-y-1">
        <h2 className="font-bold text-3xl text-bleu-nuit">Partage</h2>
        <p className="max-w-3xl text-gray-700 text-sm">
          Un lien est la porte que vous distribuez : une URL secrète qui ouvre une ou plusieurs
          expériences,{' '}
          {vitrine
            ? 'chacune dans sa dernière version en prod'
            : 'chacune en latest, la dernière version, ou, par choix explicite, épinglée sur une version précise, et chaque entrée joue un côté : dev par défaut, ou prod pour rejouer ce que joue la prod'}
          . Il vit le temps que vous choisissez et se révoque à tout moment ; les sessions déjà
          commencées continuent.
        </p>
      </header>

      {staleRead ? (
        <p role="alert" data-testid="partage-relecture-echouee" className="text-rouge-deep text-sm">
          La relecture des liens a échoué. Ce qui est affiché date de la dernière lecture réussie.
        </p>
      ) : null}

      <section data-testid="partage-liens" className="space-y-4">
        <div className="flex items-baseline gap-3">
          <h3 className="font-bold text-bleu-nuit text-xl">Liens</h3>
          <span className="text-gray-600 text-sm">{countSentence(links.data.links)}</span>
        </div>

        {links.data.links.length === 0 ? (
          <p className="text-gray-600 text-sm">
            Aucun lien pour l’instant. Le formulaire ci-dessous en crée un.
          </p>
        ) : (
          <ul className="space-y-4">
            {links.data.links.map((link) => (
              <LinkCard
                key={link.id}
                link={link}
                origin={origin}
                now={now}
                experiences={experienceCards}
                experiencesRead={experiencesRead}
                busy={writing === link.id}
                serverProblems={editingId === link.id ? problemsOf(save.error) : []}
                /*
                  The failure of a write belongs to the card that write was
                  about. `variables` is the id the mutation was called with, so
                  a dropped connection on one card's « Prolonger » no longer
                  puts a red line under every other card of the list.
                */
                failure={
                  editingId === link.id
                    ? failureOf(save.error, 'Le lien n’a pas pu être modifié. Réessayez.')
                    : ((extend.variables === link.id
                        ? failureOf(extend.error, 'Le lien n’a pas pu être prolongé. Réessayez.')
                        : null) ??
                      (revoke.variables === link.id
                        ? failureOf(revoke.error, 'Le lien n’a pas pu être révoqué. Réessayez.')
                        : null))
                }
                editing={editingId === link.id}
                onEdit={() => setEditingId(link.id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(draft) => save.mutate({ id: link.id, draft })}
                onUpdatePin={(index, pin) =>
                  save.mutate({ id: link.id, draft: linkFormWithPin(linkFormOf(link), index, pin) })
                }
                onExtend={() => extend.mutate(link.id)}
                onRevoke={() => revoke.mutate(link.id)}
              />
            ))}
          </ul>
        )}

        <p className="max-w-3xl text-gray-500 text-xs">
          Un lien expiré ou révoqué rend le même écran mort ; une entrée dont l’expérience a été
          retirée disparaît de la page de choix, et un lien à entrée unique devient mort.
        </p>
      </section>

      <section className="space-y-4">
        <div className="space-y-1">
          <h3 className="font-bold text-bleu-nuit text-xl">Créer un lien</h3>
          <p className="max-w-3xl text-gray-600 text-sm">
            Depuis ici, ou depuis le bouton « Créer un lien » de l’éditeur d’une expérience, qui
            arrive pré-rempli. L’URL secrète apparaît à la création, dans la liste.
          </p>
        </div>
        <RoleLinkForm
          key={`creation-${creationGeneration}-${search.experience ?? ''}`}
          initial={emptyLinkForm(search.experience ?? '')}
          experiences={experienceCards}
          experiencesRead={experiencesRead}
          submitLabel="Créer le lien"
          submitting={writing === 'create'}
          serverProblems={problemsOf(create.error)}
          failure={failureOf(create.error, 'Le lien n’a pas pu être créé. Réessayez.')}
          testId="partage-formulaire"
          onSubmit={(draft) => create.mutate(draft)}
        />
      </section>
    </div>
  );
}

/** « 3 liens, dont 2 actifs » (planche `ReglagesPartage`). */
function countSentence(links: readonly { state: string }[]): string {
  if (links.length === 0) return 'aucun lien';
  const active = links.filter((link) => link.state === 'active').length;
  const total = links.length === 1 ? '1 lien' : `${links.length} liens`;
  return `${total}, dont ${active === 1 ? '1 actif' : `${active} actifs`}`;
}
